import React, { useState } from 'react';
import { ArrowLeft, CheckCircle, Plane } from 'lucide-react';

interface TravelPlannerProps {
    onBack: () => void;
}

const TravelPlanner: React.FC<TravelPlannerProps> = ({ onBack }) => {
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        destinationCountry: 'UK',
        flightBookingOption: '',
        localTransportOption: '',
        requestTaxi: false
    });

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch('/api/student/submit-service', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    service: 'Travel Planner',
                    data: formData
                })
            });

            if (response.ok) {
                setSubmitted(true);
            }
        } catch (error) {
            console.error('Error submitting:', error);
        } finally {
            setLoading(false);
        }
    };

    if (submitted) {
        return (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                <div className="bg-white p-8 rounded-2xl max-w-sm w-full text-center border border-gray-100 shadow-2xl">
                    <div className="w-16 h-16 bg-[#009B8F] rounded-full flex items-center justify-center mx-auto mb-6">
                        <CheckCircle size={32} className="text-white" />
                    </div>
                    <h2 className="text-2xl font-serif font-bold text-slate-800 mb-2">Request Submitted</h2>
                    <p className="text-slate-500 text-sm mb-8 leading-relaxed">
                        We have received your travel preferences. Our team will contact you with booking options shortly.
                    </p>
                    <button
                        onClick={onBack}
                        className="w-full bg-[#011E26] hover:bg-[#009B8F] text-white font-bold py-3 px-6 rounded-lg transition-all"
                    >
                        Back to Dashboard
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto">
            <button
                onClick={onBack}
                className="flex items-center text-slate-500 hover:text-[#009B8F] mb-6 transition-colors font-medium text-sm"
            >
                <ArrowLeft size={16} className="mr-2" /> Back to Dashboard
            </button>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="bg-[#011E26] p-8 flex items-center gap-4 rounded-t-2xl">
                    <div className="bg-white/10 p-3 rounded-lg text-white">
                        <Plane size={24} />
                    </div>
                    <div>
                        <p className="text-[10px] font-bold text-[#009B8F] uppercase tracking-widest mb-1">Student Services</p>
                        <h2 className="text-2xl font-serif font-bold text-white">Travel Planner</h2>
                        <p className="text-white/60 text-sm">Organize your journey with ease.</p>
                    </div>
                </div>

                <div className="p-8">
                    <form onSubmit={handleSubmit} className="space-y-8">

                        {/* 1. Country Selection */}
                        <div>
                            <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">
                                1. Select Destination Country
                            </label>
                            <select
                                className="w-full bg-white border border-gray-200 rounded-lg px-4 py-3 text-slate-800 focus:outline-none focus:border-[#009B8F] transition-colors"
                                required
                                value={formData.destinationCountry}
                                onChange={(e) => setFormData({ ...formData, destinationCountry: e.target.value })}
                            >
                                <option value="UK">UK</option>
                                <option value="USA">USA</option>
                                <option value="Canada">Canada</option>
                                <option value="Australia">Australia</option>
                                <option value="Europe">Europe</option>
                            </select>
                        </div>

                        {/* 2. Pre-Flight Checklist */}
                        <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                            <h4 className="text-sm font-bold text-slate-700 mb-2">2. Pre-Flight Document Checklist</h4>
                            <p className="text-xs text-slate-500 mb-3">Ensure you have these ready before booking:</p>
                            <ul className="text-xs text-slate-600 space-y-2 list-disc pl-4">
                                <li>Valid Passport (6 months+ validity)</li>
                                <li>Visa Grant Letter</li>
                                <li>University Offer Letter (CAS/I-20)</li>
                                <li>TB Test / Medical Certificate</li>
                                <li>Financial Proofs</li>
                            </ul>
                        </div>

                        {/* 3. Flight Booking Options */}
                        <div>
                            <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">
                                3. Flight Booking Options
                            </label>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div
                                    onClick={() => setFormData({ ...formData, flightBookingOption: 'Self' })}
                                    className={`cursor-pointer p-4 rounded-lg border-2 transition-all ${formData.flightBookingOption === 'Self'
                                        ? 'border-[#009B8F] bg-[#009B8F]/5'
                                        : 'border-gray-200 bg-white hover:border-gray-300'
                                        }`}
                                >
                                    <h5 className="font-bold text-slate-800 mb-1">Option A: I will book my own</h5>
                                    <p className="text-xs text-slate-500">Receive a referral code for discounts.</p>
                                </div>
                                <div
                                    onClick={() => setFormData({ ...formData, flightBookingOption: 'Nex' })}
                                    className={`cursor-pointer p-4 rounded-lg border-2 transition-all ${formData.flightBookingOption === 'Nex'
                                        ? 'border-[#009B8F] bg-[#009B8F]/5'
                                        : 'border-gray-200 bg-white hover:border-gray-300'
                                        }`}
                                >
                                    <h5 className="font-bold text-slate-800 mb-1">Option B: Book through Nex</h5>
                                    <p className="text-xs text-slate-500">Let us handle the booking for you.</p>
                                </div>
                            </div>
                        </div>

                        {/* 4. Local Travel Guide */}
                        <div>
                            <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">
                                4. Local Travel Guide (After Arrival)
                            </label>
                            <select
                                className="w-full bg-white border border-gray-200 rounded-lg px-4 py-3 text-slate-800 focus:outline-none focus:border-[#009B8F] transition-colors mb-4"
                                value={formData.localTransportOption}
                                onChange={(e) => setFormData({ ...formData, localTransportOption: e.target.value })}
                            >
                                <option value="">Select Transport Preference</option>
                                <option value="Taxi">Taxi</option>
                                <option value="Uber">Uber</option>
                                <option value="Shuttle">Airport Shuttle</option>
                                <option value="Metro">Metro / Public Transport</option>
                            </select>

                            <button
                                type="button"
                                onClick={() => setFormData(prev => ({ ...prev, requestTaxi: !prev.requestTaxi }))}
                                className={`w-full py-3 rounded-lg border border-dashed transition-all flex items-center justify-center gap-2 font-bold text-sm ${formData.requestTaxi
                                    ? 'bg-[#009B8F]/10 border-[#009B8F] text-[#009B8F]'
                                    : 'border-gray-300 text-slate-400 hover:text-slate-600 hover:border-gray-400'
                                    }`}
                            >
                                {formData.requestTaxi ? <CheckCircle size={16} /> : null}
                                Request "Book a Taxi for me"
                            </button>
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full bg-[#011E26] hover:bg-[#009B8F] text-white font-bold py-4 rounded-xl transition-all"
                        >
                            {loading ? 'Submitting...' : 'Submit Travel Plan'}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default TravelPlanner;
